/**
 * formSpriteCandidates — HQ-first colour-art resolver for sub-dex FORM cells.
 *
 * WHY THIS EXISTS
 * ---------------
 * The generated form data (`pokemonForms.generated.ts`) stores each form's
 * sprite as the low-res PokeAPI Gen-5 pixel PNG
 * (`sprites/pokemon/{id}.png`, 96x96). Rendered in the sub-dex `FormGrid`
 * with `image-rendering: pixelated`, regional forms (Alolan, Galarian,
 * Hisuian, Paldean) and other alt forms looked grainy/pixelated next to the
 * 3D-looking art the rest of the app shows.
 *
 * Higher-quality renders exist for the SAME form pokemon id under the `other/`
 * tree — the Pokémon HOME 3D-style render (512x512) and the official artwork
 * (475x475). The Mega / G-Max grids already consume these via
 * `megaGmaxSprites.ts` / `pokemonArtwork.ts`, so this is an EXISTING in-app
 * source pattern, not a new runtime network dependency. All of it resolves to
 * our own mirror (spriteCdn.ts); upstream is a build-time source only.
 *
 * SOURCE PRIORITY (best first)
 *   1. HOME render       — broad form coverage, the "3D" look
 *   2. official-artwork  — premium where present (many alt forms lack it)
 *   3. original pixel sprite — guaranteed last-resort fallback (never blank)
 *
 * HOME is preferred over official-artwork for forms (the reverse of the
 * Mega/G-Max ordering) because official-artwork coverage for alt/regional
 * forms is sparse, whereas HOME has a render for nearly every form id. The
 * caller walks the list via <img> onError, so a 404 on any candidate simply
 * advances to the next — the original pixel sprite always lands last.
 */

import { SPRITE_CDN_POKEAPI, SPRITE_CDN_POKEAPI_OTHER } from "@/lib/spriteCdn";
import { FORM_GROUPS_BY_SPECIES_ID } from "@/lib/generated/pokemonForms.generated";
import {
  atlasFormSlugForFormId,
  megaFormForDisplayName,
  megaFormSpriteCandidates,
  megaGmaxAtlasFormSlug,
  megaGmaxSpriteCandidates,
} from "@/lib/megaGmaxSprites";

const POKEAPI_OTHER = SPRITE_CDN_POKEAPI_OTHER;

/**
 * Extract the trailing pokemon id from a form sprite path, e.g.
 *   .../sprites/pokemon/10091.png        -> 10091
 *   .../sprites/pokemon/shiny/10091.png  -> 10091
 * Returns null when the path does not end in a numeric id (so the caller
 * keeps the original sprite untouched rather than building a bogus URL).
 */
export function parseFormSpriteId(spritePath: string): number | null {
  const match = spritePath.match(/\/(\d+)\.png(?:[?#].*)?$/);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
}

/**
 * Ordered colour-art candidate URLs for a form, best quality first. Pass the
 * form's stored pixel `spritePath` (shiny or non-shiny — the variant is taken
 * from the path itself, so `opts.shiny` only selects the matching HQ subdir).
 *
 * Always returns at least the original `spritePath` as the final entry, so the
 * list is never empty and a form sprite is never lost even if id parsing fails
 * or every HQ render 404s.
 */
export function formSpriteCandidates(
  spritePath: string,
  opts?: {
    shiny?: boolean;
    /**
     * The base species, drawn LAST when the form's own art is a name-keyed
     * or game-icon path (a costume from the pokeminers mirror) that cannot be
     * upgraded and might not exist yet: 47 of the newest costumes have no
     * icon at the source (2026-09-10 sync), and without this the cell showed
     * the browser's broken-image glyph. The base Pokémon with the costume's
     * name under it is the honest stand-in until its art lands.
     */
    fallbackSpeciesId?: number | null;
  },
): string[] {
  const shiny = opts?.shiny ?? false;
  const id = parseFormSpriteId(spritePath);
  const urls: string[] = [];
  if (id != null) {
    // A gender-difference form is NOT a separate pokemon id — PokéAPI files it
    // under a `female/` subdirectory of the SAME id. Upgrading it by bare id
    // therefore resolves to the male render and silently replaces the artwork:
    // female Pyroar (#668) drew the maned male, reported 2026-08-07. Keep the
    // gender segment in the HQ candidates so a female render is used where one
    // exists, and let the chain fall through to the stored female pixel sprite
    // where it does not. Either way it can no longer land on the male art.
    const female = /\/female\/\d+\.png(?:[?#].*)?$/.test(spritePath);
    const genderDir = female ? "/female" : "";
    const homeDir = shiny ? "home/shiny" : "home";
    const officialDir = shiny ? "official-artwork/shiny" : "official-artwork";
    urls.push(`${POKEAPI_OTHER}/${homeDir}${genderDir}/${id}.png`);
    urls.push(`${POKEAPI_OTHER}/${officialDir}${genderDir}/${id}.png`);
  }
  // Guaranteed last-resort fallback: the original pixel sprite is always
  // present, so a form cell never goes blank.
  if (!urls.includes(spritePath)) urls.push(spritePath);
  // ...unless the "original" is itself a path with no upgrade and no
  // guarantee (see fallbackSpeciesId): then the base species stands behind it.
  const fallback = opts?.fallbackSpeciesId;
  if (id == null && fallback != null && Number.isFinite(fallback) && fallback > 0) {
    const shinyDir = shiny ? "/shiny" : "";
    urls.push(`${POKEAPI_OTHER}/${shiny ? "home/shiny" : "home"}/${fallback}.png`);
    urls.push(`${SPRITE_CDN_POKEAPI}${shinyDir}/${fallback}.png`);
  }
  return urls;
}

/**
 * Colour-art candidates for a `(species, formId)` pair, or undefined when the
 * pair names no form we hold art for.
 *
 * The step above `formSpriteCandidates`: it takes the app's own form id (e.g.
 * `darumaka-galar`) rather than a sprite path, looks the form up in the
 * generated registry, and picks the shiny or base path before building the
 * chain. Undefined means "no form art" — the caller then renders base-species
 * art, which is the correct answer for a species with no forms.
 *
 * Shared so a surface cannot half-implement forms. CaughtSprite has two
 * form-shaped props and only ONE of them changes the colour art:
 * `formSpriteCandidates` picks the artwork, while `formSlug` only selects a
 * silhouette tile for the uncaught fallback. Passing just the slug therefore
 * LOOKS like form handling and renders the base species — which is what the
 * Profile raid panel did, showing a caught Galarian Darumaka as the red Kanto
 * one (reported 2026-08-07) while the detail page's Forms grid, which goes
 * through the registry, had it right.
 */
export function speciesFormArtCandidates(
  dexNumber: number,
  formId: string | null | undefined,
  opts?: { shiny?: boolean },
): string[] | undefined {
  if (!formId) return undefined;
  const shiny = opts?.shiny ?? false;
  const form = FORM_GROUPS_BY_SPECIES_ID.get(dexNumber)?.forms.find((f) => f.formId === formId);
  if (!form) return undefined;
  const path = shiny && form.shinySpritePath ? form.shinySpritePath : form.spritePath;
  if (!path) return undefined;
  return formSpriteCandidates(path, { shiny, fallbackSpeciesId: dexNumber });
}

/**
 * One raid boss, reduced to what art resolution needs. Both boss shapes in
 * the app map onto it: the raid hub's RaidBossRef (`isMega` = tier "mega")
 * and the LeekDuck roster's RaidBossDto (`isMega` as-is, `formId` from
 * raidBossFormId). `name` is the upstream display name — for Megas it is the
 * ONLY X-vs-Y signal ("Mega Charizard Y"), since neither source carries a
 * form id for them.
 */
export interface RaidBossArtRef {
  /** Base national-dex id. */
  speciesId: number;
  isMega: boolean;
  name?: string | null;
  /** Regional-form registry id ("darumaka-galar"), when applicable. */
  formId?: string | null;
}

/**
 * Colour-art candidates for a raid boss, or undefined for plain base-species
 * art. A Mega boss resolves true Mega art (the Y form when the name says Y,
 * else the representative base/X form) — previously Mega raids drew the plain
 * species because the regional registry has no Mega entries. Non-Mega bosses
 * keep the regional-form path.
 */
export function raidBossArtCandidates(ref: RaidBossArtRef): string[] | undefined {
  if (ref.isMega) {
    const yForm = megaFormForDisplayName(ref.speciesId, ref.name);
    if (yForm) return megaFormSpriteCandidates(yForm);
    const candidates = megaGmaxSpriteCandidates("mega", ref.speciesId);
    return candidates.length > 0 ? candidates : undefined;
  }
  return speciesFormArtCandidates(ref.speciesId, ref.formId) ?? undefined;
}

/**
 * Silhouette-atlas form slug for a raid boss's uncaught/fallback shape, or
 * undefined when the base-species tile is the right one. The atlas-slug
 * counterpart of `raidBossArtCandidates`, with the same Y-by-name rule so
 * the shape can never disagree with the colour art beside it.
 */
export function raidBossSilhouetteSlug(ref: RaidBossArtRef): string | undefined {
  if (ref.isMega) {
    const yForm = megaFormForDisplayName(ref.speciesId, ref.name);
    if (yForm) return atlasFormSlugForFormId(ref.speciesId, yForm.formId);
    return megaGmaxAtlasFormSlug("mega", ref.speciesId) ?? undefined;
  }
  // Regional/costume ids have no atlas tile; this yields undefined for them.
  return atlasFormSlugForFormId(ref.speciesId, ref.formId);
}
